<template>

  <div
    class="vpay__step"
    v-loading="loading"
  >

    <EditableBlock
      class="vpay__summary"
      :mode="userSummaryMode"
      :show-button="$store.getters.userIsLoggedIn"
    >
      <template slot="display">
        <el-row>
          <el-col
            v-for="(item, i) in userSummary"
            :span="item.width"
            :key="i"
          >
            <label
              class="vpay__summary-label"
              :class="$css(styles['vpay__summary-label'])"
            >{{ item.label }}</label>
            <p class="vpay__summary-item">{{ item.value ? item.value : "N/A" }}</p>
          </el-col>
        </el-row>
      </template>
      <template slot="editable">
        <el-row>
          <el-col :span="12">
            <div
              class="vpay__input-container"
              style="padding-right: 6px"
            >
              <label>First Name</label>
              <input
                class="vpay__input vpay__input--small"
                type="text"
                v-model="$store.state.user.first_name"
              >
            </div>
          </el-col>
          <el-col :span="12">
            <div
              class="vpay__input-container"
              style="padding-left: 6px"
            >
              <label>Last Name</label>
              <input
                class="vpay__input vpay__input--small"
                type="text"
                v-model="$store.state.user.last_name"
              >
            </div>
          </el-col>
          <el-col :span="24">
            <div class="vpay__input-container">
              <label>Email Address</label>
              <input
                class="vpay__input vpay__input--small"
                type="text"
                v-model="$store.state.user.email"
              >
            </div>
          </el-col>
          <el-col :span="24">
            <div class="vpay__input-container">
              <label>Address</label>
              <vue-google-autocomplete
                id="place"
                ref="location"
                classname="vpay__input vpay__input--small"
                placeholder=""
                :value="$store.getters.userAddressString"
                :country="['au']"
                @placechanged="onChangeAddress"
                @blur="addressSelected"
              ></vue-google-autocomplete>
              <span
                class="address_error"
                v-show="addressError"
              >Please select a valid Address from the drop down list</span>
            </div>
          </el-col>
        </el-row>
      </template>
    </EditableBlock>

    <EditableBlock
      class="vpay__summary"
      :show-editable-state="false"
      @event="onPaymentSummaryEvent"
    >
      <template slot="display">
        <el-row>
          <el-col
            v-for="(item, i) in paymentSummary"
            :span="12"
            :key="i"
          >
            <label
              class="vpay__summary-label"
              :class="$css(styles['vpay__summary-label'])"
            >{{ item.label }}</label>
            <p class="vpay__summary-item">{{ item.value }}</p>
          </el-col>
        </el-row>
      </template>
    </EditableBlock>

    <el-row>
      <el-col
        v-if="savedCards.length"
        :span="24"
      >
        <el-collapse style="margin-bottom: 24px;">
          <el-collapse-item :title="`My Saved Cards (${savedCards.length})`">
            <el-radio
              class="saved-card__checkbox"
              v-model="savedCard"
              :label="false"
            >Enter new card</el-radio>
            <el-radio
              class="saved-card__checkbox"
              v-for="(card, index) in savedCards"
              v-model="savedCard"
              :label="card.token"
              :key="index"
              :disabled="isExpired(card.expiry_month, card.expiry_year)"
            ><img
                :src="getCardImageUrl(card)"
                class="saved-card__image"
              ><span class="saved-card__description">Ending {{ cardNumberFormat(card.last_4) }} (Expires XX/{{ card.expiry_year }})</span></el-radio>
          </el-collapse-item>
        </el-collapse>
      </el-col>
      <el-col
        v-if="savedCard === false"
        :span="24"
      >
        <credit-card-field
          v-model="cardDetails"
          :show-zip="false"
          :activity="cardActivity"
          @change="onCardChange"
          @valid="onCardValid"
          @invalid="onCardInvalid"
        ></credit-card-field>
        <div
          v-if="$store.getters.userIsLoggedIn"
          style="display: block; text-align: center; margin-top: 8px;"
        >
          <el-checkbox
            v-model="rememberCard"
            :disabled="disableRememberCard"
            :true-label="1"
            :false-label="0"
          >Remember Card</el-checkbox>
        </div>
      </el-col>
    </el-row>

    <div class="vpay__input-container">
      <el-button
        ref="nextButton"
        class="vpay__next-button"
        type="success"
        @click="onClickNext"
        round
      >{{ nextButtonLabel }}</el-button>
    </div>

    <div id="recaptcha-container"></div>
  </div>

</template>

<script>
import VueGoogleAutocomplete from "vue-google-autocomplete";

import EditableBlock from "@/components/EditableBlock";

import VueCreditCardField from "vue-credit-card-field";

export default {
  name: "PinPayments",

  components: {
    VueGoogleAutocomplete,
    EditableBlock,
    CreditCardField: VueCreditCardField.CreditCardField,
    InlineCreditCardField: VueCreditCardField.InlineCreditCardField,
  },

  computed: {
    styles() {
      return this.$style({
        "vpay__summary-label": {
          color: this.$store.getters.brandColour
            ? this.$store.getters.brandColour
            : "inherit",
        },
      });
    },

    paymentSummary() {
      let type = this.$store.getters.frequency.type;
      let interval = this.$store.getters.frequency.interval;
      let dateLabel = "Date";
      let amount = this.$options.filters.formatAmount(
        this.$store.getters.finalAmount
      );

      if (type === "recurring") {
        type = `${this.$options.filters.formatFrequencyType(
          type
        )} (${this.$options.filters.formatFrequencyInterval(interval)})`;
        dateLabel = "Starting on";
      } else {
        type = this.$options.filters.formatFrequencyType(type);
      }

      if (this.$store.getters.userIsPayingTheFees) {
        amount = `${amount} (Includes fees)`;
      }

      if (this.$store.getters.selectedCause.has_notes) {
        return [
          { label: "How much", value: amount },
          { label: "Towards", value: this.$store.getters.selectedCause.title },
          { label: "How often", value: type },
          {
            label: dateLabel,
            value: this.$options.filters.moment(
              this.$store.getters.frequency.startDate,
              "D MMMM YYYY"
            ),
          },
          {
            label: "Notes",
            value: this.$store.getters.notes ? this.$store.getters.notes : "-",
          },
        ];
      } else {
        return [
          { label: "How much", value: amount },
          { label: "Towards", value: this.$store.getters.selectedCause.title },
          { label: "How often", value: type },
          {
            label: dateLabel,
            value: this.$options.filters.moment(
              this.$store.getters.frequency.startDate,
              "D MMMM YYYY"
            ),
          },
        ];
      }
    },

    userSummary() {
      return [
        {
          width: 12,
          label: "Name",
          value: `${this.$store.getters.user.first_name} ${this.$store.getters.user.last_name}`,
        },
        { width: 12, label: "Email", value: this.$store.getters.user.email },
        {
          width: 24,
          label: "Address",
          value: this.$store.getters.userAddressString,
        },
      ];
    },

    userSummaryMode() {
      return this.$store.getters.userIsLoggedIn ? "display" : "edit";
    },

    nextButtonDisabled() {
      const values = [
        this.$store.getters.user.first_name,
        this.$store.getters.user.last_name,
        this.$store.getters.user.email,
        this.$store.getters.address.street_1,
        this.cardDetails.number,
        this.cardDetails.expMonth,
        this.cardDetails.expYear,
        this.cardDetails.cvc,
      ];

      return (
        values.includes(null) ||
        values.includes(undefined) ||
        values.includes("")
      );
    },

    nextButtonLabel() {
      return this.$store.getters.frequency.type === "recurring" &&
        this.$store.getters.frequency.startDate > new Date()
        ? "Set Up Recurring"
        : `Pay ${this.$options.filters.formatAmount(
            this.$store.getters.finalAmount
          )} Now`;
    },
  },

  methods: {
    onCardChange() {
      //console.log("onCardChange", arguments);
    },

    onCardValid(e) {
      this.cardActivity = true;
      setTimeout(() => {
        this.cardActivity = false;
      }, 1000);
    },

    onCardInvalid() {
      //console.log("onCardInvalid", arguments);
    },

    onPaymentSummaryEvent(e) {
      if (e.name == "edit") {
        this.$store.commit("setPaymentStep", 1);
      }
    },

    onChangeAddress(address) {
      this.addressError = false;
      this.$store.commit("address", {
        street_1: `${address.street_number} ${address.route}`,
        city: address.locality,
        state: address.administrative_area_level_1,
        postcode: address.postal_code,
        country: address.country,
      });
    },

    onClickNext() {
      grecaptcha.reset(this.captcha);
      grecaptcha.execute(this.captcha);
    },

    submitForm() {
      // Validate the form.

      const validation = this.validateForm();

      // Success or error?

      if (validation.success) {
        // Loading...

        this.loading = true;

        // Process a payment using a saved card or create a new card token?

        if (this.savedCard) {
          this.processPayment();
        } else {
          this.createCardToken();
        }
      } else {
        this.$errors.show(this.$refs.nextButton.$el, {
          message: validation.message,
        });
      }
    },

    getCardImageUrl(card) {
      return `https://www.redde.com.au/assets/logos/cards/${card.type}.png`;
    },

    validateForm() {
      // Variables.

      let response = { success: true };

      let fields = [
        {
          value: this.$store.getters.user.first_name,
          message: "Please enter your first name",
        },
        {
          value: this.$store.getters.user.last_name,
          message: "Please enter your last name",
        },
        {
          value: this.$store.getters.user.email,
          message: "Please enter your email address",
        },
        {
          value: this.$store.getters.address.street_1,
          message: "Please enter your address",
        },
      ];

      if (!this.savedCard) {
        fields = fields.concat([
          {
            value: this.cardDetails.number,
            message: "Please enter your credit card details",
          },
          {
            value: this.cardDetails.expMonth,
            message: "Please enter your credit card details",
          },
          {
            value: this.cardDetails.expYear,
            message: "Please enter your credit card details",
          },
          {
            value: this.cardDetails.cvc,
            message: "Please enter your credit card details",
          },
        ]);
      }

      // Iterate the fields and validate their values.

      const error = fields.some((field) => {
        //console.log(field, field.value, field.message, field.value === null, field.value === undefined, field.value === "");
        if (
          field.value === null ||
          field.value === undefined ||
          field.value === ""
        ) {
          return (response = { error: true, message: field.message });
        }
      });

      // if user logged in, checking card number
      if (this.$store.getters.userIsLoggedIn && !this.savedCard) {
        const card_last_4_number = this.cardDetails.number.substr(
          this.cardDetails.number.length - 4
        );
        const card_month = this.cardDetails.expMonth;
        const card_year = this.cardDetails.expYear;

        let result = this.savedCards.find(
          (item) =>
            item.last_4 == card_last_4_number &&
            item.expiry_month == card_month &&
            item.expiry_year == card_year
        );

        if (result) {
          return (response = {
            error: true,
            message: "You have a same card in your saved cards.",
          });
        }
      }

      // Return validation response.

      return response;
    },

    createCardToken() {
      this.pinPayments
        .createCardToken({
          name: `${this.$store.getters.user.first_name} ${this.$store.getters.user.last_name}`,
          number: this.cardDetails.number,
          expiry_month: this.cardDetails.expMonth,
          expiry_year: this.cardDetails.expYear,
          cvc: this.cardDetails.cvc,

          address_line1: this.$store.getters.address.street_1,
          address_city: this.$store.getters.address.city,
          address_state: this.$store.getters.address.state,
          address_postcode: this.$store.getters.address.postcode,
          address_country: this.$store.getters.address.country,
        })
        .then(
          (card, r) => {
            this.cardToken = card.token;

            this.processPayment();
          },
          (r) => {
            if (r.error && r.messages && r.messages.length) {
              this.$errors.show(this.$refs.nextButton.$el, {
                message: r.messages[0].message,
              });
            } else {
              this.$errors.show(this.$refs.nextButton.$el, {
                message: this.genericErrorMessage,
              });
            }

            this.loading = false;
          }
        )
        .done();
    },

    processPayment() {
      // Payload.

      let payload = {
        customer: {
          first_name: this.$store.getters.user.first_name,
          last_name: this.$store.getters.user.last_name,
          full_name: `${this.$store.getters.user.first_name} ${this.$store.getters.user.last_name}`,
          email: this.$store.getters.user.email,
          mobile: this.$store.getters.mobileNumberValidated,
          address: this.$store.getters.address,
          token: this.savedCard ? this.savedCard : null,
        },

        transaction: {
          description: this.$store.getters.selectedCause.title,
          amount: this.$store.getters.finalAmount,
          currency: "AUD",
          frequency: { ...this.$store.getters.frequency },
          user_paid_fees: this.$store.getters.userIsPayingTheFees,
          notes: this.$store.getters.notes,
          from_content_type: this.$store.getters.from_content_type,
          from_content_id: this.$store.getters.from_content_id,
          original_amount: this.$store.getters.amount,
          card_surcharge: this.$store.getters.creditCardFee,
        },

        product: this.$store.getters.selectedCause,

        card: {
          remember: this.savedCard ? false : this.rememberCard,
          token: this.savedCard ? null : this.cardToken,
        },

        callback_url: {
          url:
            this.$store.getters.iframeUrl.origin +
            this.$store.getters.iframeUrl.pathname,
          access_token:
            this.$store.getters.iframeUrl.searchParams.get("access-token"),
          user_token: this.$store.getters.iframeUrl.searchParams.get(
            "user-token"
          )
            ? this.$store.getters.iframeUrl.searchParams.get("user-token")
            : this.$store.getters.jwt,
          donation_causes:
            this.$store.getters.iframeUrl.searchParams.get("donation-causes"),
        },

        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      // API request.

      this.axios
        .post("transactions", [payload], {
          headers: this.$store.getters.requestHeaders,
          timeout: 60000,
        })
        .then((r) => {
          if (r.data.success) {
            this.$store.commit("transactionResponse", r.data);
            this.$store.commit("incrementPaymentStep");
          } else {
            if (r.data.code == 202 && r.data.redirect_url) {
              payload.customer.token = r.data.results.customer.token;
              payload.card.token = r.data.results.customer.token;

              localStorage.setItem("reddeData", JSON.stringify(payload));

              const windowFeatures = "popup";
              const handle = window.open(
                r.data.redirect_url,
                "_self",
                windowFeatures
              );

              if (!handle) {
                // The window wasn't allowed to open
                // This is likely caused by built-in popup blockers.
                alert("Error. You might block the build-in popup function.");
                this.$errors.show(this.$refs.nextButton.$el, {
                  message:
                    "An unknown error occurred while processing that transaction. Please try again.",
                });
              }
            } else {
              this.$errors.show(this.$refs.nextButton.$el, {
                message: r.response.data.message,
              });
            }
          }
        })
        .catch((e) => {
          if (e.response) {
            if (e.response.hasOwnProperty("data")) {
              this.$errors.show(this.$refs.nextButton.$el, {
                message: e.response.data.message,
              });
            } else if (e.response.hasOwnProperty("statusText")) {
              this.$errors.show(this.$refs.nextButton.$el, {
                message: e.response.statusText,
              });
            } else {
              this.$errors.show(this.$refs.nextButton.$el, {
                message:
                  "An unknown error occurred while processing that transaction. Please try again.",
              });
            }
          } else {
            this.$errors.show(this.$refs.nextButton.$el, {
              message:
                "An unknown error occurred while processing that transaction. Please try again.",
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    addressSelected() {
      let vm = this;
      vm.$nextTick(() => {
        if (
          vm.$store.getters.address.city == null ||
          vm.$store.getters.address.city == undefined ||
          vm.$store.getters.address.city == ""
        ) {
          vm.addressError = true;
        } else {
          vm.addressError = false;
        }
      });
    },

    // Bug fix LEG-257 26/04/2022
    // backend pass the numberic data to frontend, so there will be an issue
    // that a string starts with 0, it will be converted to a number.
    // e.g. 0430 will be 430, so we need to add 0 in front of it
    cardNumberFormat(cardNumber) {
      cardNumber = cardNumber.toString();

      var carNumber_length = cardNumber.length;

      if (carNumber_length < 4) {
        for (let i = 0; i < 4 - carNumber_length; i++) {
          cardNumber = "0" + cardNumber;
        }
      }

      return cardNumber;
    },

    isExpired(month, year) {
      const cardExpiredDateTime = new Date(year, month, 0, 23, 59, 59);
      return cardExpiredDateTime < new Date();
    },
  },

  mounted() {
    var vm = this;

    grecaptcha.ready(function () {
      if (vm.captcha == null) {
        vm.captcha = grecaptcha.render("recaptcha-container", {
          sitekey: "6Lfh4xkeAAAAAJSH0WOJ6glP5kD9_dePX97CBbLo",
          callback: vm.submitForm,
          size: "invisible",
        });
      }
    });

    // Store reference to Pin Payments API.

    this.pinPayments = new Pin.Api(
      this.$store.getters.provider.settings.publishable_key,
      this.$store.getters.account.sandbox ? "test" : "live"
    );

    // Get saved cards?

    if (this.$store.getters.userIsLoggedIn) {
      this.axios
        .get(`${process.env.VUE_APP_USERS_API_URL}saved-cards`, {
          headers: this.$store.getters.requestHeaders2,
        })
        .then((r) => {
          this.$set(this, "savedCards", r.data.success ? r.data.results : []);
        });
    }

    if (this.$store.getters.error3DS) {
      var that = this;

      setTimeout(() => {
        this.$errors.show(that.$refs.nextButton.$el, {
          message: that.$store.getters.error3DS,
        });
      }, 1000);
    }
  },

  data() {
    const recurringPayment = this.$store.getters.frequency.type === "recurring";

    return {
      captcha: null,

      genericErrorMessage:
        "Uh oh... an unknown error occurred while processing your transaction. Please try again.",

      firstName: this.$store.getters.user.first_name,
      lastName: this.$store.getters.user.last_name,
      emailAddress: this.$store.getters.user.email,

      pinPayments: null,

      cardToken: null,
      cardDetails: {},
      cardActivity: false,

      savedCards: [],
      savedCard: false,

      rememberCard: recurringPayment ? 1 : 0,
      disableRememberCard: recurringPayment,

      loading: false,
      addressError: false,
    };
  },
};
</script>

<style>
.pin-form-field {
  height: 2em;
  width: 50%;
  border: 1px solid black;
  padding: 0 0.5em;
}
.credit-card-field input::placeholder {
  /* Chrome, Firefox, Opera, Safari 10.1+ */
  color: #ccc;
  opacity: 0.5;
}
.credit-card-field.has-errors .invalid-feedback {
  display: none !important;
}
.address_error {
  color: red;
}
</style>
